import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import defaultTexts from '../../texts/texts'
import constants from '../../config/constants'
import { request } from '../../_network/request'
import { clientAccountsURLs } from '../../_network/apiUrls'
import { capitalizeFirstLetter } from '../_common/_helpers/string'
import { getTextedDate } from '../subscriptions/_helpers/dates'
import CustomBackdrop from '../_common/CustomBackdrop'
import CustomDialog from '../_common/CustomDialog'
import ChangePasswordWrapper from './ChangePasswordWrapper'
import ExternalApplicationsWrapper from './ExternalApplicationsWrapper'

const ProfileWrapper = (props) => {
  const theme = useTheme()

  const user_role = useSelector(state => state.profile.user_role)
  const editPermission = user_role === 'superadmin' || user_role === 'owner' || user_role === 'admin'

  const [isLoading, setIsLoading] = useState(false)
  const [profile, setProfile] = useState(null)
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false)
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false)
  const [notificationDialogProps, setNotificationDialogProps] = useState(null)

  const pageLabelStyle = {
    color: theme.palette.vodka,
    fontSize: '18px',
    fontWeight: '500',
    padding: '0px 0px 20px 20px',
  }

  const wrapperStyle = {
    background: theme.palette.aliceBlue,
    borderRadius: '50px 20px 20px 20px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  }

  const userInfoLabelStyle = {
    height: '60px',
    background: theme.palette.greenSheen,
    color: 'white',
    fontSize: '20px',
    fontWeight: '500',
    borderRadius: '50px 0px',
    padding: '0px 10%',    
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const accountInfoLabelStyle = {
    height: '60px',
    background: theme.palette.violetsAreBlue,
    color: 'white',
    fontSize: '20px',
    fontWeight: '500',
    borderRadius: '50px 0px',
    padding: '0px 10%',    
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const bodyStyle = {
    minHeight: '400px',
    padding: '40px 20px',
  }

  const keyStyle = {
    color: theme.palette.grayX11,
    fontSize: '18px',
    fontWeight: '500',
    width: '200px',
  }

  const valueStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '18px',
    fontWeight: '500',
    wordBreak: 'break-all',
  }

  const changePasswordButtonStyle = {
    fontWeight: '500',
    color: theme.palette.violetsAreBlue,
    border: `${theme.palette.violetsAreBlue} 1px solid`,
    background: theme.palette.vodka,
    fontSize: '18px',
    textTransform: 'capitalize',
    height: '50px',
    borderRadius: '25px',
    padding: '0px 40px',

    '&:hover': {
      background: theme.palette.violetsAreBlue,
      color: 'white',
    },
  }

  const handleChangePasswordDialogOpen = () => {
    setChangePasswordDialogOpen(true)
  }

  const handleChangePasswordDialogClose = () => {
    setChangePasswordDialogOpen(false)
  }

  const handleNotificationDialogOpen = (props) => {
    setNotificationDialogOpen(true)
    setNotificationDialogProps(props)
  }

  const handleNotificationDialogClose = () => {
    setNotificationDialogOpen(false)
    setNotificationDialogProps(null)
  }

  useEffect(() => {
    const fetchProfileData = async () => {
      setIsLoading(true)

      const url = constants.core_service_domain + '/' + clientAccountsURLs.getProfile      
      const request_data = { url, method: 'GET' }    
      const response = await request(request_data)
      
      if (response && response.status_code && response.status_code < 400 && response.data) {
        setProfile(response.data)
      } else {
        setProfile(null)
      }

      setIsLoading(false)
    }

    fetchProfileData()
  }, [])
  
  return (
    <>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      <Box sx={{ padding: '40px 20px' }}>
        <Box sx={pageLabelStyle}>{defaultTexts.accountSettings}</Box>
        <Grid container spacing={8}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Box sx={wrapperStyle}>
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='flex-start'
              >
                <Box sx={userInfoLabelStyle}>{defaultTexts.userInfo}</Box>
              </Stack>
              <Box sx={bodyStyle}>
                {
                  profile &&
                  <>
                    <Stack
                      direction='row'
                      alignItems='flex-start'
                      justifyContent='flex-start'
                      sx={{ marginBottom: '20px' }}
                    >
                      <Box sx={keyStyle}>{defaultTexts.username}:</Box>
                      <Box sx={valueStyle}>{profile.username}</Box>
                    </Stack>

                    <Stack
                      direction='row'
                      alignItems='flex-start'
                      justifyContent='flex-start'
                      sx={{ marginBottom: '20px' }}
                    >
                      <Box sx={keyStyle}>{defaultTexts.email}:</Box>
                      <Box sx={valueStyle}>{profile.email}</Box>
                    </Stack>

                    <Stack
                      direction='row'
                      alignItems='flex-start'
                      justifyContent='flex-start'
                      sx={{ marginBottom: '20px' }}
                    >
                      <Box sx={keyStyle}>{defaultTexts.name}:</Box>
                      <Box sx={valueStyle}>{profile.firstName}</Box>
                    </Stack>

                    <Stack
                      direction='row'
                      alignItems='flex-start'
                      justifyContent='flex-start'
                      sx={{ marginBottom: '20px' }}
                    >
                      <Box sx={keyStyle}>{defaultTexts.lastName}:</Box>
                      <Box sx={valueStyle}>{profile.lastName}</Box>
                    </Stack>

                    <Stack
                      direction='row'
                      alignItems='flex-start'
                      justifyContent='flex-start'
                      sx={{ marginBottom: '40px' }}
                    >
                      <Box sx={keyStyle}>{defaultTexts.roles}:</Box>
                      <Box sx={valueStyle}>{ (profile.roles && profile.roles.length > 0) ? capitalizeFirstLetter(profile.roles[0]) : '' }</Box>
                    </Stack>

                    <Button
                      variant='contained'
                      disableRipple
                      disableElevation
                      onClick={handleChangePasswordDialogOpen}
                      sx={changePasswordButtonStyle}
                    >
                      {defaultTexts.changePassword}
                    </Button>
                  </>
                }
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Box sx={wrapperStyle}>
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='flex-start'
              >
                <Box sx={accountInfoLabelStyle}>{defaultTexts.accountInfo}</Box>
              </Stack>
              <Box sx={bodyStyle}>
                {
                  profile &&
                  <>
                    <Stack
                      direction='row'
                      alignItems='flex-start'
                      justifyContent='flex-start'
                      sx={{ marginBottom: '20px' }}
                    >
                      <Box sx={keyStyle}>{defaultTexts.accountId}:</Box>
                      <Box sx={valueStyle}>{profile.account_id}</Box>
                    </Stack>

                    <Stack
                      direction='row'
                      alignItems='flex-start'
                      justifyContent='flex-start'
                      sx={{ marginBottom: '20px' }}
                    >
                      <Box sx={keyStyle}>{defaultTexts.status}:</Box>
                      <Box
                        sx={{
                          ...valueStyle,
                          color: profile.enabled ? theme.palette.greenSheen : theme.palette.coralRed,
                        }}
                      >
                        { profile.enabled ? defaultTexts.active : defaultTexts.disabled }
                      </Box>
                    </Stack>

                    <Stack
                      direction='row'
                      alignItems='flex-start'
                      justifyContent='flex-start'
                      sx={{ marginBottom: '20px' }}
                    >
                      <Box sx={keyStyle}>{defaultTexts.createdAt}:</Box>
                      <Box sx={valueStyle}>{getTextedDate(profile.account_created)}</Box>
                    </Stack>
                  </>
                }
              </Box>
            </Box>
          </Grid>
        </Grid>
        { editPermission && <ExternalApplicationsWrapper /> }
      </Box>

      <CustomDialog
        open={changePasswordDialogOpen}
        onClose={handleChangePasswordDialogClose}
        dialogTitle={{
          title: defaultTexts.changePassword, 
          sx: {
            color: theme.palette.violetsAreBlue,
            textTransform: 'uppercase',
            textAlign: 'center',
            fontSize: '32px',
          }
        }}
        backgroundColor={theme.palette.oxfordBlue}
        closeIconColor={theme.palette.violetsAreBlue}
      >
        <ChangePasswordWrapper
          handleChangePasswordDialogClose={handleChangePasswordDialogClose}
          handleNotificationDialogOpen={handleNotificationDialogOpen}
        />
      </CustomDialog>

      <CustomDialog
        open={notificationDialogOpen}
        onClose={handleNotificationDialogClose}
        dialogTitle={{
          title: (notificationDialogProps && notificationDialogProps.title) ? notificationDialogProps.title : '',
          sx: {
            color: theme.palette.violetsAreBlue,
            textTransform: 'uppercase',
            textAlign: 'center',
            fontSize: '32px',
          }
        }}
        dialogBody={{
          text: (notificationDialogProps && notificationDialogProps.text) ? notificationDialogProps.text : '',
          sx: {
            color: 'white',
            textAlign: 'center',
            fontSize: '18px',
          }
        }}
        backgroundColor={theme.palette.oxfordBlue}
        closeIconColor={theme.palette.violetsAreBlue}
      />
    </>
  )
}

export default ProfileWrapper